import { useState } from "react";
import { toast } from "sonner";
import { Flag, ShieldCheck, Loader2 } from "lucide-react";
import { Seo } from "@/components/web/Seo";
import { MaskedLines, FadeUp } from "@/components/web/Reveal";
import { submitReport } from "@/lib/scanApi";

const CATEGORIES = ["Phishing", "Scam / Fraud", "Malware", "Impersonation", "Other"];

const isValidUrl = (value) => {
  try {
    const u = new URL(value.match(/^https?:\/\//i) ? value : `https://${value}`);
    return u.hostname.includes(".");
  } catch {
    return false;
  }
};

export default function Report() {
  const [form, setForm] = useState({ url: "", category: "", description: "", email: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [duplicate, setDuplicate] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.url.trim() || !isValidUrl(form.url)) errs.url = "Please enter a valid URL.";
    if (!form.category) errs.category = "Please choose a category.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    try {
      const result = await submitReport(form);
      setDuplicate(Boolean(result.duplicate));
      setStatus("success");
      toast.success("Report received", {
        description: "Thank you for helping protect the community.",
      });
    } catch (err) {
      setStatus("idle");
      toast.error("Could not submit report", {
        description: err.message || "Please try again in a moment.",
      });
    }
  };

  return (
    <>
      <Seo
        title="Report a Link — LinkExpln"
        description="Report a suspicious, phishing or malicious link to LinkExpln. Community reports help protect everyone from scams and malware."
      />

      <section className="pt-24 pb-16 sm:pt-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">Community Defense</p>
          <h1 data-testid="report-heading" className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            <MaskedLines lines={["SEEN SOMETHING?", "REPORT IT."]} />
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed">
            Every report makes the shield stronger. Flag a suspicious link and
            help protect the next person who receives it.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="border border-white/10 bg-[#0A121D] p-6 sm:p-10">
              {status === "success" ? (
                <div data-testid="report-success-state" className="border border-[#00C5FE]/30 bg-[#00C5FE]/5 p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <ShieldCheck size={24} className="text-[#00C5FE] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-mono-ls text-[11px] tracking-[0.2em] uppercase text-[#00C5FE] mb-2">
                        STATUS: REPORT_RECEIVED
                      </p>
                      <h2 className="font-heading font-bold text-lg text-white">
                        {duplicate ? "This link is already on our radar." : "Report received. Thank you."}
                      </h2>
                      <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
                        {duplicate
                          ? "Someone recently reported this same URL, so it's already queued for review. Thank you for confirming it — every report strengthens the signal."
                          : "Your report has been securely recorded and queued for review by our security team. Thank you for looking out for others."}
                      </p>
                      <button
                        data-testid="report-reset-button"
                        onClick={() => { setStatus("idle"); setDuplicate(false); setForm({ url: "", category: "", description: "", email: "", website: "" }); }}
                        className="mt-5 font-mono-ls text-xs tracking-[0.15em] uppercase text-[#00C5FE] hover:text-white transition-colors duration-200 underline underline-offset-4"
                      >
                        Report another link
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div>
                    <label htmlFor="report-url" className="ls-overline block mb-2">Suspicious URL *</label>
                    <input
                      id="report-url"
                      data-testid="report-url-input"
                      type="text"
                      inputMode="url"
                      value={form.url}
                      onChange={set("url")}
                      className="w-full bg-black border border-white/15 px-4 py-3.5 font-mono-ls text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent"
                      placeholder="https://suspicious-link.example"
                    />
                    {errors.url && <p data-testid="report-url-error" className="mt-2 font-mono-ls text-[11px] text-[#FF3366]" role="alert">{errors.url}</p>}
                  </div>

                  <div>
                    <span className="ls-overline block mb-3">Threat category *</span>
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Threat category">
                      {CATEGORIES.map((c) => (
                        <button
                          key={c}
                          type="button"
                          data-testid={`report-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          onClick={() => setForm({ ...form, category: c })}
                          aria-pressed={form.category === c}
                          className={`px-4 py-2 font-mono-ls text-[11px] tracking-[0.12em] uppercase border transition-colors duration-200 ${
                            form.category === c
                              ? "btn-brand text-black border-transparent"
                              : "border-white/15 text-zinc-400 hover:border-[#00C5FE]/50 hover:text-white"
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                    {errors.category && <p data-testid="report-category-error" className="mt-2 font-mono-ls text-[11px] text-[#FF3366]" role="alert">{errors.category}</p>}
                  </div>

                  <div>
                    <label htmlFor="report-description" className="ls-overline block mb-2">What happened? (optional)</label>
                    <textarea
                      id="report-description"
                      data-testid="report-description-input"
                      rows={4}
                      value={form.description}
                      onChange={set("description")}
                      className="w-full bg-black border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent resize-y"
                      placeholder="Where did you receive this link? What made it suspicious?"
                    />
                  </div>

                  <div>
                    <label htmlFor="report-email" className="ls-overline block mb-2">Your email (optional)</label>
                    <input
                      id="report-email"
                      data-testid="report-email-input"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      className="w-full bg-black border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent"
                      placeholder="For follow-up only — never shared"
                    />
                  </div>

                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={set("website")}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  <button
                    type="submit"
                    data-testid="report-submit-button"
                    disabled={status === "submitting"}
                    className="inline-flex items-center gap-2 btn-brand disabled:opacity-60 text-black font-heading font-bold px-8 py-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:ring-offset-2 focus:ring-offset-black"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 size={17} className="animate-spin" /> Submitting…
                      </>
                    ) : (
                      <>
                        <Flag size={17} strokeWidth={2.5} /> Submit Report
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
